/* A shareable sheet for one item (Equipment, Items & Consumables), laid out like the SRD
   stat card on Adversaries and Environments, with Download (PNG) and Share buttons. Share
   opens the device's share sheet when there is one and copies the item's link otherwise. */

import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { Download, Share2, X } from 'lucide-react';
import { StatBox, StatItem } from './StatBlock';
import { downloadPng } from '../utils/downloadPng';
import './CardSheet.css';
import './ItemSheet.css';

export interface ItemLinkTarget {
  href: string;
  onOpen: () => void;
}

/** An item's name as a real link to its sheet: a plain click opens the sheet in place; ctrl, middle click and copy link work as usual. */
export function ItemLink({ href, onOpen, children }: ItemLinkTarget & { children: ReactNode }) {
  const open = (e: MouseEvent) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onOpen();
  };
  return <a className="item-link" href={href} onClick={open}>{children}</a>;
}

/** Click handler for a whole table row or card: opens the item, except on links and buttons inside it. */
export const openOnClick = (onOpen: () => void) => (e: MouseEvent) => {
  if (!(e.target as Element).closest('a, button, [role="button"]')) onOpen();
};

interface ItemSheetProps {
  title: string;
  /** The SRD-style kind line, e.g. "Tier 1 Primary Weapon" */
  kind: string;
  stats: StatItem[][];
  source?: string;
  href: string;
  onClose: () => void;
  children?: ReactNode;
}

export default function ItemSheet({ title, kind, stats, source, href, onClose, children }: ItemSheetProps) {
  const sheetRef = useRef<HTMLElement>(null);
  const [note, setNote] = useState('');
  const url = new URL(href, window.location.origin).href;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch { /* closed without sharing */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setNote('Link copied');
    } catch {
      setNote('Could not copy the link');
    }
  };

  return (
    <div className="card-modal-overlay item-sheet-overlay" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <article ref={sheetRef} className="srdcard item-sheet" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="card-sheet__close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        <h2 className="srdcard__name">{title}</h2>
        <p className="srdcard__kind">{kind}</p>
        <StatBox rows={stats} />
        {children}
        {source && <span className="source-label">{source}</span>}
        <p className="item-sheet__footer">dhtools.net</p>
      </article>
      <div className="item-sheet__actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="card-download-btn" onClick={() => sheetRef.current && downloadPng(sheetRef.current, `${title.replace(/\s+/g, '-').toLowerCase()}.png`)}>
          <Download size={16} aria-hidden="true" /> Download
        </button>
        <button type="button" className="card-download-btn" onClick={share}>
          <Share2 size={16} aria-hidden="true" /> Share
        </button>
        {note && <span className="item-sheet__note" role="status">{note}</span>}
      </div>
    </div>
  );
}
